import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { FileText, FilePdf, Image, Upload, Trash, DownloadSimple, CloudArrowUp, Lock, FolderOpen, Eye } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const DOC_TYPES = [
  { value: "general", label: "General" },
  { value: "501c3", label: "501(c)(3) Letter" },
  { value: "bylaws", label: "Bylaws" },
  { value: "board_list", label: "Board List" },
  { value: "annual_report", label: "Annual Report" },
  { value: "financial_statement", label: "Financial Statement" },
  { value: "letter_of_support", label: "Letter of Support" },
  { value: "project_budget", label: "Project Budget" },
  { value: "resume", label: "Resume / CV" },
  { value: "funder_report", label: "Funder Report" },
  { value: "other", label: "Other" }
];

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const FileIcon = ({ type }) => {
  if (type === "pdf") return <FilePdf weight="duotone" className="w-8 h-8 text-red-400" />;
  if (["png", "jpg", "jpeg"].includes(type)) return <Image weight="duotone" className="w-8 h-8 text-blue-400" />;
  return <FileText weight="duotone" className="w-8 h-8 text-zinc-400" />;
};

const Documents = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [limitInfo, setLimitInfo] = useState(null);
  const [docType, setDocType] = useState("general");
  const [dragOver, setDragOver] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const res = await axios.get(`${API}/documents`);
      setDocuments(res.data.documents || []);
      setLimitInfo(res.data.limit_info);
    } catch (error) {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;

    for (const file of files) {
      const ext = file.name.split(".").pop().toLowerCase();
      if (!["pdf", "png", "jpg", "jpeg"].includes(ext)) {
        toast.error(`${file.name}: File type not supported. Use PDF, PNG, or JPG.`);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: File too large. Maximum 10MB.`);
        continue;
      }

      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("doc_type", docType);

      try {
        await axios.post(`${API}/documents/upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        toast.success(`${file.name} uploaded successfully!`);
      } catch (error) {
        const detail = error.response?.data?.detail || "Upload failed";
        toast.error(detail);
      }
    }

    setUploading(false);
    fetchDocuments();
  };

  const handleDelete = async (docId, filename) => {
    if (!window.confirm(`Delete "${filename}"? This cannot be undone.`)) return;

    try {
      await axios.delete(`${API}/documents/${docId}`);
      toast.success("Document deleted");
      fetchDocuments();
    } catch {
      toast.error("Failed to delete document");
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleUpload(e.dataTransfer.files);
  };

  const atLimit = limitInfo && limitInfo.used >= limitInfo.limit;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="documents-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Documents</h1>
            <p className="text-zinc-400 mt-1">Upload and manage your nonprofit documents</p>
          </div>
          {limitInfo && (
            <p className="text-sm text-zinc-500" data-testid="doc-limit-info">
              {limitInfo.used} / {limitInfo.limit} documents used
              {atLimit && <span className="text-amber-400 ml-2">(limit reached)</span>}
            </p>
          )}
        </div>

        {/* Upload Area */}
        <Card className={`bg-zinc-950 border-zinc-800 ${dragOver ? 'border-emerald-500 bg-emerald-500/5' : ''} ${atLimit ? 'opacity-60' : ''}`}>
          <CardContent className="p-6">
            <div
              className="border-2 border-dashed border-zinc-700 rounded-sm p-8 text-center cursor-pointer hover:border-zinc-500 transition-colors"
              onDragOver={(e) => { e.preventDefault(); if (!atLimit) setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={atLimit ? (e) => e.preventDefault() : handleDrop}
              onClick={() => !atLimit && fileInputRef.current?.click()}
              data-testid="upload-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                multiple
                className="hidden"
                onChange={(e) => handleUpload(e.target.files)}
                data-testid="file-input"
              />
              {atLimit ? (
                <>
                  <Lock weight="duotone" className="w-10 h-10 mx-auto mb-3 text-zinc-600" />
                  <p className="text-zinc-400 font-medium">Document limit reached</p>
                  <p className="text-sm text-zinc-500 mt-1">Upgrade to Premium for unlimited uploads</p>
                  <Button
                    onClick={(e) => { e.stopPropagation(); navigate("/pricing"); }}
                    className="mt-4 bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
                    data-testid="upgrade-docs-btn"
                  >
                    Upgrade to Premium
                  </Button>
                </>
              ) : (
                <>
                  <CloudArrowUp weight="duotone" className="w-10 h-10 mx-auto mb-3 text-emerald-400" />
                  <p className="text-zinc-300 font-medium">
                    {uploading ? "Uploading..." : "Drop files here or click to browse"}
                  </p>
                  <p className="text-sm text-zinc-500 mt-1">PDF, PNG, JPG — up to 10MB each</p>
                </>
              )}
            </div>

            {/* Document Type Selector */}
            {!atLimit && (
              <div className="flex items-center gap-3 mt-4">
                <span className="text-sm text-zinc-400">Upload as:</span>
                <Select value={docType} onValueChange={setDocType}>
                  <SelectTrigger className="w-[200px] bg-zinc-900 border-zinc-700" data-testid="doc-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    {DOC_TYPES.map((dt) => (
                      <SelectItem key={dt.value} value={dt.value}>{dt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Documents List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="loading-spinner" style={{ width: 32, height: 32 }} />
          </div>
        ) : documents.length === 0 ? (
          <Card className="bg-zinc-950 border-zinc-800">
            <CardContent className="py-16 text-center">
              <FolderOpen weight="duotone" className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
              <p className="text-zinc-400 font-medium">No documents uploaded yet</p>
              <p className="text-sm text-zinc-500 mt-1">Upload your 501(c)(3), bylaws, or other key documents to get started</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {documents.map((doc) => (
              <Card
                key={doc.doc_id}
                className="bg-zinc-950 border-zinc-800 hover:border-zinc-700 transition-colors"
                data-testid={`doc-card-${doc.doc_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    {/* File Icon */}
                    <div className="flex-shrink-0">
                      <FileIcon type={doc.file_type} />
                    </div>

                    {/* File Info */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate" data-testid={`doc-name-${doc.doc_id}`}>{doc.filename}</p>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-zinc-500">
                        <span className="px-1.5 py-0.5 bg-zinc-900 border border-zinc-800 rounded-sm text-zinc-400">
                          {DOC_TYPES.find(dt => dt.value === doc.doc_type)?.label || doc.doc_type}
                        </span>
                        <span>{formatFileSize(doc.file_size)}</span>
                        <span>{formatDate(doc.created_at)}</span>
                        <span className="uppercase text-zinc-600">{doc.file_type}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {["png", "jpg", "jpeg"].includes(doc.file_type) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setPreviewDoc(doc)}
                          className="text-zinc-400 hover:text-white"
                          data-testid={`preview-doc-${doc.doc_id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      )}
                      <a href={`${API}/documents/${doc.doc_id}/download`} target="_blank" rel="noopener noreferrer">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-zinc-400 hover:text-white"
                          data-testid={`download-doc-${doc.doc_id}`}
                        >
                          <DownloadSimple className="w-4 h-4" />
                        </Button>
                      </a>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(doc.doc_id, doc.filename)}
                        className="text-zinc-400 hover:text-red-400"
                        data-testid={`delete-doc-${doc.doc_id}`}
                      >
                        <Trash className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Image Preview Modal */}
        {previewDoc && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            onClick={() => setPreviewDoc(null)}
          >
            <div className="max-w-3xl max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
              <img
                src={`${API}/documents/${previewDoc.doc_id}/download`}
                alt={previewDoc.filename}
                className="max-w-full rounded-sm"
              />
              <p className="text-center text-sm text-zinc-400 mt-3">{previewDoc.filename}</p>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Documents;
